import { Col, Row, Form } from "react-bootstrap";
import Button from "@mui/material/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { Grid } from "@mui/material";
import { useLoader } from "../../context/LoaderContext";
import {
  getAccountData,
  processStageInsertUpdate,
} from "../../api/processStageApi/processStageApi";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";

interface ProcessStageActionProps {
  businessUnitData: any;
}

const ProcessStageAction: React.FC<ProcessStageActionProps> = ({
  businessUnitData,
}) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const { rowData, action } = location.state || {};
  const [accountData, setAccountData] = useState<any>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const [formData, setFormData] = useState({
    accountName: accountData[0]?.accountName || "",
    processStageName: rowData?.processStageName || "",
    aliasName: rowData?.aliasName || "",
  });
  const [isActive, setIsActive] = useState(true);

  const [formErrors, setFormErrors] = useState({
    accountName: "",
    processStageName: "",
    aliasName: "",
  });

  const handleCancelClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed) {
      navigate("/processStageDisplay");
    }
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "processStageName" || name === "aliasName") {
      validateText(value, name);
    }
  };

  useEffect(() => {
    if (accountData && accountData.length > 0 && rowData) {
      setFormData({
        accountName: accountData[0]?.accountName || "",
        processStageName: rowData?.processStageName || "",
        aliasName: rowData?.aliasName || "",
      });
    }
  }, [accountData, rowData]);

  useEffect(() => {
    if (action === "Edit" && accountData.length > 0) {
      const selectedAccount = accountData.find(
        (account: any) =>
          account.contextName ===
          (businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"))
      );
      if (selectedAccount) {
        setFormData((prevData) => ({
          ...prevData,
          accountName: selectedAccount.accountName,
        }));
      }
    }
  }, [action, accountData, businessUnitData]);

  const getAllAccountData = async () => {
    setLoading(true);
    const payload = {
      contextName:
        businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getAccountData(payload);
      setAccountData(data);
    } catch (error) {
      showSnackbar(`Error fetching data ${error}`, true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllAccountData();
  }, []);

  const validateText = (text: string, fieldName: keyof typeof formErrors) => {
    const textPattern = /^[a-zA-Z\s]+$/;
    if (!textPattern.exec(text)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "Please enter alphabets only",
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    }
  };

  const handleUpdateClick = async (event: React.FormEvent) => {
    event.preventDefault();
    let errors = {
      accountName: "",
      processStageName: "",
      aliasName: "",
    };
    if (!formData.accountName) {
      errors.accountName = "Please select a customer.";
    }
    if (!formData.processStageName.trim()) {
      errors.processStageName = "Please enter a stage name.";
    }
    if (!formData.aliasName.trim()) {
      errors.aliasName = "Please enter an alias name.";
    }
    setFormErrors(errors);
    const hasErrors = Object.values(errors).some((error) => error !== "");
    if (!hasErrors) {
      const payload = {
        stageName: formData.processStageName,
        processStageId: rowData.processStageId,
        aliasName: formData.aliasName,
        updatedBy: LocalStorageUtil.getItem("userId"),
        contextName:
          businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
        active: isActive,
      };
      await processStageInsertUpdate(payload);
      navigate("/processStageDisplay");
      showSnackbar("Process stage – Alias name Updated successfully");
    }
  };

  const handleActiveDeactive = () => {
    setIsActive(!isActive);
  };

  return (
    <div>
      {action === "Edit" ? (
        <NavigationBar title="Process Stages" text="Update Process Stages" />
      ) : (
        <NavigationBar title="Process Stages" text="Create Process Stage" />
      )}
      <div
        className="ag-theme-quartz-dark"
        style={{ marginTop: "20px", marginLeft: "20px" }}
      >
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{ color: "var(--aggrid-text-color)",fontSize: "11px" }}
                >
                  Customer<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  name="accountName"
                  value={formData.accountName}
                  onChange={handleChange}
                  disabled={action === "Edit"}
                  style={{fontSize: "11px"}}
                >
                  {action === "Edit"
                    ? accountData
                        .filter(
                          (account: any) =>
                            account.contextName ===
                            (businessUnitData?.contextName ||
                              LocalStorageUtil.getItem("contextName"))
                        )
                        .map((account: any) => (
                          <option key={account.id} value={account.accountName}>
                            {account.accountName}
                          </option>
                        ))
                    : accountData.map((account: any) => (
                        <>
                          <option value="">Select customer</option>
                          <option key={account.id} value={account.accountName}>
                            {account.accountName}
                          </option>
                        </>
                      ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{ color: "var(--aggrid-text-color)" ,fontSize: "11px"}}
                >
                  Process Stage Name<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Menu name"
                  name="processStageName"
                  value={formData.processStageName}
                  onChange={handleChange}
                  required
                  isInvalid={!!formErrors.processStageName}
                  disabled={action === "Edit"}
                  style={{fontSize: "11px"}}
                />
                {formErrors.processStageName && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.processStageName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{ color: "var(--aggrid-text-color)",fontSize: "11px" }}
                >
                  Alias Name<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Please Enter the Alias name"
                  name="aliasName"
                  value={formData.aliasName}
                  onChange={handleChange}
                  required
                  isInvalid={!!formErrors.aliasName}
                  style={{fontSize: "11px"}}
                />
                {formErrors.aliasName && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.aliasName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button
                style={{
                  color: "var(--aggrid-text-color)",
                  backgroundColor: "var(--button-background-color)",
                   marginTop: "2.4em",fontSize: "11px",
                }}
              >
                <Form.Check
                  type="checkbox"
                  checked={isActive}
                  onChange={handleActiveDeactive}
                  style={{  margin: "1px 3px" ,
                    textTransform: "none",
                    fontSize: "11px",
                    color: isActive ? "#154360" : "#7B241C",
                    backgroundColor: isActive ? "#D6EAF8" : "#FADBD8",
                    padding: "1px 3px",
                    borderRadius: "3px",
                    alignItems: "center",
                    height: "1.5em", }}
                />
                Active
              </Button>
            </Col>
          </Row>
        </Form>
        <Grid
          item
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          {action === "Edit" ? (
            <Button
              variant="contained"
              className="update-button"
              onClick={handleUpdateClick}
            >
              Update
            </Button>
          ) : (
            <Button variant="contained" className="update-button">
              Create
            </Button>
          )}
          <Button
            variant="contained"
            className="cancel-button"
            style={{ marginRight: "20px" }}
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
          <ConfirmDialog
            open={confirmDialogOpen}
            onClose={handleConfirmDialogClose}
            title="Process Stages - Update"
            content="Are you sure you want to Cancel?"
          />
        </Grid>
      </div>
    </div>
  );
};

export default ProcessStageAction;
